import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SocialMetaTags from './SocialMetaTags';
import { apiFetch, getApiHost } from '../utils/api';

interface FigureSummary {
  figureKey: string;
  displayName: string;
  pseudonymous: boolean;
  xHandle: string | null;
  aliases?: Array<{ handle: string; status?: string }>;
  trustLevel: string | null;
  publicStature: string | null;
  publicSentiment: string | null;
  reputationSummary: string | null;
  mitigates: boolean;
  walletCount: number;
  associatedTokens?: Array<{ mint: string; symbol: string | null; relationship: string; via: string }>;
  researchedAt: string | null;
}

// X profile photo via unavatar (no auth). The X avatar is public and safe to show
// even for pseudonymous figures (it's their chosen public image). Falls back to
// the figure's initial if there's no handle or the avatar can't be resolved.
const Avatar: React.FC<{ handle: string | null; name: string }> = ({ handle, name }) => {
  const [failed, setFailed] = useState(false);
  if (handle && !failed) {
    return (
      <img
        src={`https://unavatar.io/twitter/${encodeURIComponent(handle)}?fallback=false`}
        alt={name}
        onError={() => setFailed(true)}
        className="w-11 h-11 rounded-full object-cover border border-gray-700 flex-shrink-0 bg-crypto-dark"
      />
    );
  }
  return (
    <div className="w-11 h-11 rounded-full bg-crypto-accent/15 border border-crypto-accent/30 flex items-center justify-center flex-shrink-0">
      <span className="text-lg font-bold text-crypto-accent">{(name || '?').charAt(0).toUpperCase()}</span>
    </div>
  );
};

const FiguresIndexPage: React.FC = () => {
  const [figures, setFigures] = useState<FigureSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`${getApiHost()}/api/figures`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setFigures(data.figures || []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load figures');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return figures;
    return figures.filter(f =>
      (f.displayName || '').toLowerCase().includes(q) ||
      (f.xHandle || '').toLowerCase().includes(q) ||
      (f.figureKey || '').toLowerCase().includes(q) ||
      (f.aliases || []).some(a => (a.handle || '').toLowerCase().includes(q)));
  }, [figures, query]);

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <SocialMetaTags
        title="Known Figures — deepnets"
        description="Public-figure profiles behind Solana wallets and tokens: developers, influencers, and personalities identified by deepnets research."
        image={`${window.location.origin}/logo192.png`}
        url={`${window.location.origin}/figures`}
        type="website"
      />
      <Header />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 py-8 w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-1">Known Figures</h1>
          <p className="text-gray-400 text-sm">
            Public personas identified behind Solana wallets — developers, influencers, and personalities.
            Assessments are algorithmic and may be revised.
          </p>
        </div>

        <div className="relative mb-6">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or @handle…"
            className="w-full bg-crypto-gray border border-gray-700 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-crypto-accent"
          />
        </div>

        {loading && <div className="text-gray-400 py-16 text-center">Loading figures…</div>}
        {error && <div className="text-crypto-red py-16 text-center">Failed to load: {error}</div>}

        {!loading && !error && (
          <>
            <div className="text-xs text-gray-500 mb-3">
              {filtered.length} {filtered.length === 1 ? 'figure' : 'figures'}
              {query && ` matching “${query}”`}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map(f => (
                <Link
                  key={f.figureKey}
                  to={`/figures/${f.figureKey}`}
                  className="bg-crypto-card border border-gray-800 rounded-xl p-4 hover:border-crypto-accent/60 transition flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Avatar handle={f.xHandle} name={f.displayName} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {/* displayName is the MAIN name we refer to the figure by —
                            it's fine if it differs from the X handle; that's not an
                            "alias". Aliases are only ADDITIONAL names beyond this + the handle. */}
                        <span className="font-semibold truncate">{f.displayName}</span>
                      </div>
                      {f.xHandle && <div className="text-xs text-gray-500 truncate">@{f.xHandle}</div>}
                    </div>
                  </div>

                  {f.reputationSummary && (
                    <p className="text-xs text-gray-400 leading-relaxed line-clamp-2 mb-3 flex-1">{f.reputationSummary}</p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap mt-auto">
                    {/* trust/stature/sentiment kept internal for now — not shown */}
                    {f.walletCount > 0 && (
                      <span className="text-[10px] text-gray-500">
                        <i className="fas fa-wallet mr-1" />{f.walletCount} {f.walletCount === 1 ? 'wallet' : 'wallets'}
                      </span>
                    )}
                  </div>

                  {/* Associated-token chips. Non-anchor spans — the card itself
                      is a <Link>, so nested anchors aren't allowed. */}
                  {f.associatedTokens && f.associatedTokens.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap mt-2">
                      {f.associatedTokens.slice(0, 4).map(t => (
                        <span
                          key={t.mint + t.relationship}
                          className="text-[10px] text-gray-300 bg-crypto-dark border border-gray-700 rounded px-1.5 py-0.5"
                          title={`${t.relationship}${t.via === 'curated' ? ' · curated' : ''}`}
                        >
                          ${t.symbol || t.mint.slice(0, 4)}
                        </span>
                      ))}
                      {f.associatedTokens.length > 4 && (
                        <span className="text-[10px] text-gray-500">+{f.associatedTokens.length - 4}</span>
                      )}
                    </div>
                  )}
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-gray-500 py-16 text-center">No figures match “{query}”.</div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default FiguresIndexPage;
